import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../auth/AuthContext";
import {
  getGuestStorageSnapshot,
  removeGuestSearch,
  removeGuestSpot,
  removeImportedGuestLibraryItems,
  saveGuestSearch,
  saveGuestSpot,
  type GuestSavedSearch,
  type GuestSavedSpot,
  type GuestStorageSnapshot
} from "../services/guest-storage";
import {
  importGuestLibrary,
  isSyncableSpotId,
  loadAccountLibrary,
  removeAccountSearch,
  removeAccountSpot,
  saveAccountSearch,
  saveAccountSpot,
  type AccountLibrarySnapshot,
  type GuestImportResult
} from "./accountSavedLibrary";
import { accountLibraryCacheKey, clearAccountLibraryCache } from "./cache";

export type SavedSpot = GuestSavedSpot;
export type SavedSearch = GuestSavedSearch;
export type SavedLibrarySnapshot = AccountLibrarySnapshot;

type SavedLibraryContextValue = {
  mode: "guest" | "account";
  snapshot: SavedLibrarySnapshot;
  loading: boolean;
  mutating: boolean;
  error: string | null;
  guestImport: {
    pending: boolean;
    dismissed: boolean;
    importableSpotCount: number;
    searchCount: number;
    skippedSpotCount: number;
  };
  saveSpot: (spot: Omit<SavedSpot, "savedAt">) => Promise<void>;
  removeSpot: (spotId: string) => Promise<void>;
  saveSearch: (search: Omit<SavedSearch, "id" | "createdAt">) => Promise<void>;
  removeSearch: (searchId: string) => Promise<void>;
  importGuestData: () => Promise<GuestImportResult>;
  dismissGuestImport: () => void;
  refresh: () => Promise<void>;
};

const emptyAccountLibrary: AccountLibrarySnapshot = {
  savedSpots: [],
  savedSearches: []
};

const SavedLibraryContext = createContext<SavedLibraryContextValue | null>(null);

export function SavedLibraryProvider({ children }: { children: React.ReactNode }) {
  const { loading: authLoading, session } = useAuth();
  const userId = session?.user.id ?? null;
  const [guestSnapshot, setGuestSnapshot] = useState<GuestStorageSnapshot>(() => getGuestStorageSnapshot());
  const [accountSnapshot, setAccountSnapshot] = useState<AccountLibrarySnapshot>(emptyAccountLibrary);
  const [accountOwnerId, setAccountOwnerId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [mutating, setMutating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [importDismissed, setImportDismissed] = useState(false);
  const previousUserIdRef = useRef<string | null>(null);

  const commitAccountSnapshot = useCallback((nextSnapshot: AccountLibrarySnapshot, ownerId: string) => {
    setAccountOwnerId(ownerId);
    setAccountSnapshot(nextSnapshot);
    writeAccountCache(ownerId, nextSnapshot);
  }, []);

  const refresh = useCallback(async () => {
    if (!userId) {
      setGuestSnapshot(getGuestStorageSnapshot());
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const nextSnapshot = await loadAccountLibrary(userId);
      commitAccountSnapshot(nextSnapshot, userId);
    } catch (caughtError) {
      setError(errorMessage(caughtError));
      throw caughtError;
    } finally {
      setLoading(false);
    }
  }, [commitAccountSnapshot, userId]);

  useEffect(() => {
    let active = true;
    setError(null);
    setGuestSnapshot(getGuestStorageSnapshot());

    if (authLoading) {
      return () => {
        active = false;
      };
    }

    if (!userId) {
      if (previousUserIdRef.current) {
        clearAccountLibraryCache(previousUserIdRef.current);
      }
      previousUserIdRef.current = null;
      setAccountOwnerId(null);
      setAccountSnapshot(emptyAccountLibrary);
      setLoading(false);
      setImportDismissed(false);
      return () => {
        active = false;
      };
    }

    const cached = readAccountCache(userId);
    previousUserIdRef.current = userId;
    setAccountOwnerId(userId);
    setAccountSnapshot(cached ?? emptyAccountLibrary);
    setImportDismissed(readImportDismissal(userId));
    setLoading(true);

    void loadAccountLibrary(userId)
      .then((nextSnapshot) => {
        if (active) {
          commitAccountSnapshot(nextSnapshot, userId);
        }
      })
      .catch((caughtError) => {
        if (active) {
          setError(errorMessage(caughtError));
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [authLoading, commitAccountSnapshot, userId]);

  const saveSpot = useCallback(async (spot: Omit<SavedSpot, "savedAt">) => {
    setMutating(true);
    setError(null);
    try {
      if (!userId) {
        const result = saveGuestSpot(spot);
        if (!result.persisted) {
          throw new Error("Local storage is unavailable. This Spot was not saved.");
        }
        setGuestSnapshot(result.snapshot);
        return;
      }

      if (!isSyncableSpotId(spot.spotId)) {
        const result = saveGuestSpot(spot);
        if (!result.persisted) {
          throw new Error("Local storage is unavailable. This demonstration Spot was not saved.");
        }
        setGuestSnapshot(result.snapshot);
        return;
      }

      const savedSpot = await saveAccountSpot(userId, spot);
      const currentAccountSnapshot = accountOwnerId === userId ? accountSnapshot : emptyAccountLibrary;
      commitAccountSnapshot({
        ...currentAccountSnapshot,
        savedSpots: [
          savedSpot,
          ...currentAccountSnapshot.savedSpots.filter((item) => item.spotId !== spot.spotId)
        ]
      }, userId);
    } catch (caughtError) {
      setError(errorMessage(caughtError));
      throw caughtError;
    } finally {
      setMutating(false);
    }
  }, [accountOwnerId, accountSnapshot, commitAccountSnapshot, userId]);

  const removeSpot = useCallback(async (spotId: string) => {
    setMutating(true);
    setError(null);
    try {
      if (!userId) {
        const result = removeGuestSpot(spotId);
        if (!result.persisted) {
          throw new Error("Local storage is unavailable. This Spot was not removed.");
        }
        setGuestSnapshot(result.snapshot);
        return;
      }

      if (!isSyncableSpotId(spotId)) {
        const result = removeGuestSpot(spotId);
        if (!result.persisted) {
          throw new Error("Local storage is unavailable. This demonstration Spot was not removed.");
        }
        setGuestSnapshot(result.snapshot);
        return;
      }

      await removeAccountSpot(userId, spotId);
      const currentAccountSnapshot = accountOwnerId === userId ? accountSnapshot : emptyAccountLibrary;
      commitAccountSnapshot({
        ...currentAccountSnapshot,
        savedSpots: currentAccountSnapshot.savedSpots.filter((spot) => spot.spotId !== spotId)
      }, userId);
    } catch (caughtError) {
      setError(errorMessage(caughtError));
      throw caughtError;
    } finally {
      setMutating(false);
    }
  }, [accountOwnerId, accountSnapshot, commitAccountSnapshot, userId]);

  const saveSearch = useCallback(async (search: Omit<SavedSearch, "id" | "createdAt">) => {
    setMutating(true);
    setError(null);
    try {
      if (!userId) {
        const result = saveGuestSearch(search);
        if (!result.persisted) {
          throw new Error("Local storage is unavailable. This search was not saved.");
        }
        setGuestSnapshot(result.snapshot);
        return;
      }

      const savedSearch = await saveAccountSearch(userId, search);
      const currentAccountSnapshot = accountOwnerId === userId ? accountSnapshot : emptyAccountLibrary;
      commitAccountSnapshot({
        ...currentAccountSnapshot,
        savedSearches: [
          savedSearch,
          ...currentAccountSnapshot.savedSearches.filter((item) => item.id !== savedSearch.id)
        ]
      }, userId);
    } catch (caughtError) {
      setError(errorMessage(caughtError));
      throw caughtError;
    } finally {
      setMutating(false);
    }
  }, [accountOwnerId, accountSnapshot, commitAccountSnapshot, userId]);

  const removeSearch = useCallback(async (searchId: string) => {
    setMutating(true);
    setError(null);
    try {
      if (!userId) {
        const result = removeGuestSearch(searchId);
        if (!result.persisted) {
          throw new Error("Local storage is unavailable. This search was not removed.");
        }
        setGuestSnapshot(result.snapshot);
        return;
      }

      await removeAccountSearch(userId, searchId);
      const currentAccountSnapshot = accountOwnerId === userId ? accountSnapshot : emptyAccountLibrary;
      commitAccountSnapshot({
        ...currentAccountSnapshot,
        savedSearches: currentAccountSnapshot.savedSearches.filter((search) => search.id !== searchId)
      }, userId);
    } catch (caughtError) {
      setError(errorMessage(caughtError));
      throw caughtError;
    } finally {
      setMutating(false);
    }
  }, [accountOwnerId, accountSnapshot, commitAccountSnapshot, userId]);

  const importGuestData = useCallback(async () => {
    if (!userId) {
      throw new Error("Sign in before importing device saves.");
    }

    setMutating(true);
    setError(null);
    const currentGuestSnapshot = getGuestStorageSnapshot();
    try {
      const result = await importGuestLibrary(userId, currentGuestSnapshot);
      const nextAccountSnapshot = await loadAccountLibrary(userId);
      const localResult = removeImportedGuestLibraryItems({
        spotIds: result.importedSpotIds,
        searchIds: result.importedSearchIds
      });

      if (!localResult.persisted) {
        throw new Error("The account import completed, but device saves could not be cleared.");
      }

      commitAccountSnapshot(nextAccountSnapshot, userId);
      setGuestSnapshot(localResult.snapshot);
      setImportDismissed(false);
      return result;
    } catch (caughtError) {
      setError(errorMessage(caughtError));
      throw caughtError;
    } finally {
      setMutating(false);
    }
  }, [commitAccountSnapshot, userId]);

  const dismissGuestImport = useCallback(() => {
    setImportDismissed(true);
    if (userId) {
      writeImportDismissal(userId);
    }
  }, [userId]);

  const guestImport = useMemo(() => {
    const importableSpotCount = guestSnapshot.savedSpots.filter((spot) => isSyncableSpotId(spot.spotId)).length;
    const skippedSpotCount = guestSnapshot.savedSpots.length - importableSpotCount;
    const searchCount = guestSnapshot.savedSearches.length;
    return {
      pending: Boolean(userId && (importableSpotCount + skippedSpotCount + searchCount > 0)),
      dismissed: importDismissed,
      importableSpotCount,
      searchCount,
      skippedSpotCount
    };
  }, [guestSnapshot.savedSearches.length, guestSnapshot.savedSpots, importDismissed, userId]);

  const value = useMemo<SavedLibraryContextValue>(() => ({
    mode: userId ? "account" : "guest",
    snapshot: !userId
      ? guestSnapshot
      : accountOwnerId === userId
        ? {
            ...accountSnapshot,
            savedSpots: [
              ...accountSnapshot.savedSpots,
              ...guestSnapshot.savedSpots.filter((spot) => !isSyncableSpotId(spot.spotId))
            ]
          }
        : {
            savedSpots: guestSnapshot.savedSpots.filter((spot) => !isSyncableSpotId(spot.spotId)),
            savedSearches: []
          },
    loading: authLoading || loading || Boolean(userId && accountOwnerId !== userId),
    mutating,
    error,
    guestImport,
    saveSpot,
    removeSpot,
    saveSearch,
    removeSearch,
    importGuestData,
    dismissGuestImport,
    refresh
  }), [
    accountSnapshot,
    accountOwnerId,
    authLoading,
    dismissGuestImport,
    error,
    guestImport,
    guestSnapshot,
    importGuestData,
    loading,
    mutating,
    refresh,
    removeSearch,
    removeSpot,
    saveSearch,
    saveSpot,
    userId
  ]);

  return <SavedLibraryContext.Provider value={value}>{children}</SavedLibraryContext.Provider>;
}

export function useSavedLibrary() {
  const context = useContext(SavedLibraryContext);
  if (!context) {
    throw new Error("useSavedLibrary must be used within SavedLibraryProvider.");
  }
  return context;
}

function importDismissalKey(userId: string) {
  return `dahlini.guest-import-dismissed.v1.${userId}`;
}

function readAccountCache(userId: string): AccountLibrarySnapshot | null {
  try {
    const raw = window.localStorage.getItem(accountLibraryCacheKey(userId));
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as Partial<AccountLibrarySnapshot>;
    return {
      savedSpots: Array.isArray(parsed.savedSpots) ? parsed.savedSpots.filter(isSavedSpot) : [],
      savedSearches: Array.isArray(parsed.savedSearches) ? parsed.savedSearches.filter(isSavedSearch) : []
    };
  } catch {
    return null;
  }
}

function writeAccountCache(userId: string, snapshot: AccountLibrarySnapshot) {
  try {
    window.localStorage.setItem(accountLibraryCacheKey(userId), JSON.stringify(snapshot));
  } catch {
    // A cache failure must not turn a successful account write into a failed action.
  }
}

function readImportDismissal(userId: string) {
  try {
    return window.sessionStorage.getItem(importDismissalKey(userId)) === "1";
  } catch {
    return false;
  }
}

function writeImportDismissal(userId: string) {
  try {
    window.sessionStorage.setItem(importDismissalKey(userId), "1");
  } catch {
    // Session-only dismissal is optional.
  }
}

function isSavedSpot(value: unknown): value is SavedSpot {
  if (!value || typeof value !== "object") {
    return false;
  }
  const spot = value as Partial<SavedSpot>;
  return Boolean(spot.spotId && spot.name && spot.waterbody && spot.location && spot.savedAt);
}

function isSavedSearch(value: unknown): value is SavedSearch {
  if (!value || typeof value !== "object") {
    return false;
  }
  const search = value as Partial<SavedSearch>;
  return Boolean(search.id && search.label && search.filters && search.createdAt);
}

function errorMessage(error: unknown) {
  return error instanceof Error ? error.message : "Saved data could not be synchronized.";
}
